const DEFAULT_CHUNK_INTERVAL_MS = 1000;
const DEFAULT_MIME_TYPES = [
  'video/webm; codecs=vp8,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=h264,aac',
  'video/mp4'
];
const ACK_TIMEOUT_MS = 10000;
const RESULT_TIMEOUT_MS = 120000;
const MAX_BUFFERED_BYTES = 4 * 1024 * 1024;

function isLocalHostname(hostname) {
  return ['localhost', '127.0.0.1', '0.0.0.0'].includes(String(hostname || '').toLowerCase());
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function dateStamp(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('');
}

function timeStamp(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return [
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds())
  ].join('');
}

function toWsUrl(url) {
  if (!url) return '';
  if (url.startsWith('ws://') || url.startsWith('wss://')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^http/i, 'ws');
  }
  return url;
}

function cameraStreamUrlFromApiBase(apiBase) {
  if (!apiBase) return '';

  try {
    const resolved = new URL(apiBase);
    const pageUsesHttps = typeof location !== 'undefined' && location.protocol === 'https:';

    if (isLocalHostname(resolved.hostname)) {
      const protocol = pageUsesHttps ? 'wss:' : 'ws:';
      const port = pageUsesHttps ? '4443' : (resolved.port || '4000');
      return `${protocol}//${resolved.hostname}:${port}/camera-stream`;
    }

    resolved.protocol = resolved.protocol === 'https:' ? 'wss:' : 'ws:';
    resolved.pathname = '/camera-stream';
    resolved.search = '';
    resolved.hash = '';
    return resolved.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
}

export function resolveCameraStreamCandidates() {
  const configured = import.meta.env.VITE_CAMERA_STREAM_WS_URL;

  if (configured) {
    return [toWsUrl(configured)];
  }

  const apiStreamUrl = cameraStreamUrlFromApiBase(import.meta.env.VITE_API_BASE_URL);

  if (typeof location === 'undefined') {
    return unique([apiStreamUrl, 'ws://localhost:4000/camera-stream', 'wss://localhost:4443/camera-stream']);
  }

  if (!isLocalHostname(location.hostname)) {
    if (apiStreamUrl) return [apiStreamUrl];
    const currentWsOrigin = `${location.protocol}//${location.host}`.replace(/^http/i, 'ws');
    return [`${currentWsOrigin}/camera-stream`];
  }

  const currentWsOrigin = `${location.protocol}//${location.host}`.replace(/^http/i, 'ws');
  const fallbacks = location.protocol === 'https:'
    ? ['wss://localhost:4443/camera-stream', 'ws://localhost:4000/camera-stream']
    : ['ws://localhost:4000/camera-stream', 'wss://localhost:4443/camera-stream'];

  return unique([`${currentWsOrigin}/camera-stream`, apiStreamUrl, ...fallbacks]);
}

export function getSupportedStreamMimeType(preferredMimeType = '') {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }

  const candidates = unique([preferredMimeType, ...DEFAULT_MIME_TYPES]);
  return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
}

export function createCameraSessionId(userId = 'guest') {
  const safeUserId = String(userId || 'guest').replace(/[^A-Za-z0-9_-]/g, '_');
  const random = Math.random().toString(36).slice(2, 8);
  const now = new Date();
  return `cam_${safeUserId}_${dateStamp(now)}_${timeStamp(now)}_${random}`;
}

export function stopCameraTracks(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

function sendJson(socket, payload) {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
    return true;
  }

  return false;
}

function createDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

function withTimeout(promise, timeoutMs, message) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    })
  ]).finally(() => clearTimeout(timer));
}

async function waitForSocketBuffer(socket) {
  while (socket.readyState === WebSocket.OPEN && socket.bufferedAmount > MAX_BUFFERED_BYTES) {
    await new Promise((resolve) => setTimeout(resolve, 20));
  }
}

export class CameraStreamClient {
  constructor({
    url = '',
    candidates = null,
    onStatus = () => { },
    onMessage = () => { },
    onError = () => { },
    onResult = () => { },
    onChunk = () => { }
  } = {}) {
    this.candidates = url ? [toWsUrl(url)] : candidates || resolveCameraStreamCandidates();
    this.onStatus = onStatus;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onResult = onResult;
    this.onChunk = onChunk;
    this.socket = null;
    this.recorder = null;
    this.mediaStream = null;
    this.sessionId = '';
    this.totalBytes = 0;
    this.totalChunks = 0;
    this.pendingAck = null;
    this.pendingResult = null;
    this.sendQueue = Promise.resolve();
    this.sendError = null;
  }

  async connect() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return this.socket;
    }

    let lastError = null;

    for (const url of this.candidates) {
      try {
        this.onStatus(`Dang ket noi ${url}`);
        this.socket = await this.openSocket(url);
        return this.socket;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Cannot connect camera stream WebSocket');
  }

  openSocket(url) {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(url);
      let settled = false;

      socket.binaryType = 'arraybuffer';

      const fail = (error) => {
        if (settled) return;
        settled = true;
        reject(error instanceof Error ? error : new Error(`WebSocket failed: ${url}`));
      };

      socket.addEventListener('open', () => {
        if (settled) return;
        settled = true;
        this.attachSocketHandlers(socket);
        this.onStatus('Da ket noi camera stream');
        resolve(socket);
      }, { once: true });

      socket.addEventListener('error', fail, { once: true });
      socket.addEventListener('close', () => {
        if (!settled) fail(new Error(`WebSocket closed before open: ${url}`));
      }, { once: true });
    });
  }

  attachSocketHandlers(socket) {
    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;

      let message;
      try {
        message = JSON.parse(event.data);
      } catch (error) {
        console.warn('Invalid camera stream message:', event.data);
        return;
      }

      this.onMessage(message);

      if (message.type === 'SERVER_ACK' && message.sessionId === this.sessionId) {
        this.pendingAck?.resolve(message);
        this.pendingAck = null;
        this.onStatus('Server dang nhan stream');
        return;
      }

      if (message.type === 'SERVER_RESULT' && message.sessionId === this.sessionId) {
        this.pendingResult?.resolve(message);
        this.pendingResult = null;
        this.onResult(message);
        this.onStatus('Stream da hoan tat');
        return;
      }

      if (message.type === 'SERVER_ERROR') {
        const error = new Error(message.message || 'Camera stream server error');
        this.pendingAck?.reject(error);
        this.pendingAck = null;
        this.pendingResult?.reject(error);
        this.pendingResult = null;
        this.onError(error);
        this.onStatus(error.message);
      }
    });

    socket.addEventListener('close', () => {
      const error = new Error('Camera stream WebSocket đã đóng trước khi hoàn tất');
      this.pendingAck?.reject(error);
      this.pendingAck = null;
      this.pendingResult?.reject(error);
      this.pendingResult = null;
      this.onStatus('Đã ngắt kết nối luồng camera');
    });
  }

  async start({
    mediaStream,
    sessionId = '',
    userId = 0,
    cameraName = 'Client Camera',
    inputType = '',
    outputType = 'mp4',
    recordingStream = 2,
    hasAudio = true,
    hasVideo = true,
    preferredMimeType = 'video/webm; codecs=vp8,opus',
    chunkIntervalMs = DEFAULT_CHUNK_INTERVAL_MS,
    videoBitsPerSecond = null,
    audioBitsPerSecond = null,
    ffmpegPreset = 'veryfast',
    crf = 23
  } = {}) {
    if (!mediaStream) {
      throw new Error('MediaStream is required before starting stream');
    }

    if (this.recorder?.state === 'recording') {
      return this;
    }

    this.mediaStream = mediaStream;
    this.sessionId = sessionId || createCameraSessionId(userId);
    this.totalBytes = 0;
    this.totalChunks = 0;
    this.sendQueue = Promise.resolve();
    this.sendError = null;

    const socket = await this.connect();
    const mimeType = getSupportedStreamMimeType(preferredMimeType);
    const resolvedInputType = inputType || (mimeType.includes('mp4') ? 'mp4' : 'webm');
    const recorderOptions = {
      ...(mimeType ? { mimeType } : {}),
      ...(videoBitsPerSecond ? { videoBitsPerSecond } : {}),
      ...(audioBitsPerSecond ? { audioBitsPerSecond } : {})
    };

    this.pendingAck = createDeferred();
    this.pendingResult = createDeferred();
    this.pendingResult.promise.catch(() => { });

    sendJson(socket, {
      type: 'START_STREAM',
      sessionId: this.sessionId,
      userId,
      cameraName,
      inputType: resolvedInputType,
      outputType,
      recordingStream,
      hasAudio,
      hasVideo,
      ffmpegPreset,
      crf
    });

    await withTimeout(this.pendingAck.promise, ACK_TIMEOUT_MS, 'Server không phản hồi yêu cầu bắt đầu stream');

    this.recorder = new MediaRecorder(mediaStream, recorderOptions);
    this.recorder.ondataavailable = (event) => {
      if (!event.data || event.data.size <= 0) return;
      const blob = event.data;

      this.sendQueue = this.sendQueue.then(async () => {
        if (socket.readyState !== WebSocket.OPEN) {
          throw new Error('Mất kết nối camera stream khi đang gửi video');
        }

        await waitForSocketBuffer(socket);
        const buffer = await blob.arrayBuffer();
        socket.send(buffer);
        this.totalChunks += 1;
        this.totalBytes += buffer.byteLength;
        this.onChunk({
          sessionId: this.sessionId,
          index: this.totalChunks - 1,
          bytes: buffer.byteLength,
          totalBytes: this.totalBytes,
          totalChunks: this.totalChunks
        });
      }).catch((error) => {
        this.sendError ||= error;
        this.onError(error);
      });
    };
    this.recorder.onerror = (event) => {
      const error = event.error || new Error('MediaRecorder error');
      this.onError(error);
      this.onStatus(error.message);
    };

    this.recorder.start(chunkIntervalMs);
    this.onStatus('Đang stream camera');
    return this;
  }

  async stop({
    stopTracks = true,
    closeSocket = true,
    waitForResult = true
  } = {}) {
    const socket = this.socket;
    const recorder = this.recorder;
    const resultPromise = this.pendingResult?.promise || null;

    try {
      if (recorder && recorder.state !== 'inactive') {
        const stopped = new Promise((resolve) => {
          recorder.addEventListener('stop', resolve, { once: true });
        });
        recorder.requestData();
        recorder.stop();
        await stopped;
        await this.sendQueue;
        if (this.sendError) throw this.sendError;
      }

      const stopSent = sendJson(socket, {
        type: 'STOP_STREAM',
        sessionId: this.sessionId
      });

      if (!stopSent) {
        throw new Error('Không thể gửi lệnh dừng camera stream');
      }

      if (waitForResult && resultPromise) {
        return await withTimeout(
          resultPromise,
          RESULT_TIMEOUT_MS,
          'Server xử lý video quá thời gian cho phép'
        );
      }

      return null;
    } finally {
      if (stopTracks) {
        stopCameraTracks(this.mediaStream);
      }

      if (closeSocket && socket?.readyState === WebSocket.OPEN) {
        socket.close(1000, 'client-stop');
      }

      this.recorder = null;
      this.mediaStream = null;
    }
  }

  close({ stopTracks = true } = {}) {
    if (this.recorder?.state === 'recording') {
      this.recorder.stop();
    }

    if (stopTracks) {
      stopCameraTracks(this.mediaStream);
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.close(1000, 'client-close');
    }

    this.recorder = null;
    this.mediaStream = null;
    this.socket = null;
  }
}

export function createCameraStreamClient(options = {}) {
  return new CameraStreamClient(options);
}
